// Libraries
import { TriangleDownIcon } from '@chakra-ui/icons';
import { Box, Button, Center, Flex, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
// Utils
import text from '@translations/en.json';
import { Rating } from '@utils/index';
// Types
import { RatingKeys } from '@globalTypes/index';
import { OptimizeButtonProps } from './OptimizeButton.types';

/**
 * The `OptimizeButton` component is a button that triggers a dropdown menu on click. The dropdown menu lists
 * all possible "Ratings" options. Each option, when clicked, invokes the provided `onClick` handler with the
 * respective rating as an argument.
 *
 * @component
 * @param {OptimizeButtonProps} props - The props to configure the OptimizeButton component.
 * @param {(rating: RatingKeys) => void} props.onClick - A callback function that is called when a rating in the dropdown menu is clicked. The clicked rating is passed as an argument.
 *
 * @returns {ReactElement} The rendered OptimizeButton component.
 */
export const OptimizeButton = ({ onClick }: OptimizeButtonProps) => {
  return (
    <Menu>
      <MenuButton
        as={Button}
        colorScheme='submitScheme'
        h={8}
        p={0}
        w={28}
        sx={{
          span: {
            display: 'inline-block',
            h: 'full',
          },
        }}
      >
        <Flex align='center' h='full'>
          <Center flex={1} h='full' borderRight='lightWhite'>
            <Text color='white' fontSize='s' fontWeight='medium'>
              {text.optimize}
            </Text>
          </Center>
          <Center px={1.5}>
            <TriangleDownIcon boxSize={2} />
          </Center>
        </Flex>
      </MenuButton>
      <MenuList p={0} w={28} minW={28}>
        {Object.keys(Rating).map((rating) => {
          return (
            <MenuItem key={`optimize-${rating}`} px={2} onClick={() => onClick(rating as RatingKeys)}>
              <Flex align='center' justify='space-between' gap={1}>
                <Text fontSize='m'>{`${text.cii_rating}: ${rating}`}</Text>
                <Box boxSize={3.5} borderRadius='base' bg={`ratings.${rating}.dark`} />
              </Flex>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};
